// views/Profile/components/BalanceCard.jsx
import React from 'react';
import {
  Coins,
  RefreshCw,
  AlertCircle,
  ArrowUpRight,
  Users,
} from 'lucide-react';
import bigZenitCoin from '../../../assets/big-zenit-coin.webp';
import './BalanceCard.css';

const formatCoins = (value) => {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US').format(value);
};

// La barra de uso se fue de acá por dos motivos, y el segundo era un bug.
//
// El primero: la rosquilla de "Usage this period", justo al lado, dice el
// mismo porcentaje. Una métrica importante tiene una representación
// principal, no dos pegadas.
//
// El segundo: el cálculo estaba mal. Era `allowance - periodBalance`, y con
// saldo comprado por encima de la asignación eso da negativo; el
// `Math.max(0, ...)` lo aplastaba a cero y la tarjeta decía "0% of 5,000
// used" al lado de "11,012 of 5,000". Un número mal calculado que además
// estaba duplicado no se arregla: se quita.

/**
 * BalanceCard — multi-variant balance display.
 *
 * Variants:
 *   - 'account' (default): personal-account or non-Enterprise org
 *     account. Hero number + period/extra chips.
 *   - 'managed-by-org': org account whose balance lives in the
 *     Enterprise pool (`organization.isEnterprisePool === true`).
 *     Hides numerics and exposes a "Contact admin" CTA — the user
 *     can't see the pool and shouldn't be misled by a per-account
 *     number.
 *
 * The 'org-pool' variant (the admin-only view of the org pool itself)
 * lands in Fase 8.
 */
export default function BalanceCard({
  variant = 'account',
  totalBalance,
  periodBalance,
  extraBalance,
  monthlyAllowance,
  organizationName,
  loading = false,
  refreshing = false,
  error = null,
  onRefresh,
  onGetMore,
}) {
  if (variant === 'managed-by-org') {
    return (
      <section
        className="balance-card balance-card--managed"
        role="status"
        aria-live="polite"
      >
        <header className="balance-card__header">
          <span className="balance-card__label">
            <Users size={16} aria-hidden="true" />
            Coins managed by your organization
          </span>
        </header>
        <div className="balance-card__managed-body">
          <p className="balance-card__managed-text">
            Your usage is charged to the Enterprise plan
            {organizationName ? (
              <>
                {' of '}
                <strong>{organizationName}</strong>
              </>
            ) : null}
            . Ask an organization admin to see the pool balance.
          </p>
        </div>
      </section>
    );
  }


  if (loading) {
    return (
      <section className="balance-card balance-card--loading" aria-busy="true">
        <div className="balance-card__header">
          <span className="balance-card__label">Available Balance</span>
        </div>
        <div className="balance-card__hero balance-card__hero--skeleton" />
        <div className="balance-card__chips">
          <span className="balance-card__chip balance-card__chip--skeleton" />
          <span className="balance-card__chip balance-card__chip--skeleton" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="balance-card balance-card--error">
        <div className="balance-card__header">
          <span className="balance-card__label">Available Balance</span>
        </div>
        <div className="balance-card__error">
          <AlertCircle size={20} />
          <p>{error.message || 'Unable to load balance'}</p>
          {error.requestId && (
            <small className="balance-card__request-id">
              ref: {error.requestId}
            </small>
          )}
          {onRefresh && (
            <button
              type="button"
              className="balance-card__retry"
              onClick={onRefresh}
            >
              Retry
            </button>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="balance-card">
      <header className="balance-card__header">
        <span className="balance-card__label">
          <Coins size={16} aria-hidden="true" />
          Available Balance
        </span>
        {onRefresh && (
          <button
            type="button"
            className={`balance-card__icon-btn${
              refreshing ? ' balance-card__icon-btn--spinning' : ''
            }`}
            onClick={onRefresh}
            aria-label="Refresh balance"
            disabled={refreshing}
          >
            <RefreshCw size={16} aria-hidden="true" />
          </button>
        )}
      </header>

      <div className="balance-card__hero">
        <img
          src={bigZenitCoin}
          alt=""
          aria-hidden="true"
          className="balance-card__coin"
        />
        <span className="balance-card__hero-number">
          {formatCoins(totalBalance)}
        </span>
      </div>

      <ul className="balance-card__chips" aria-label="Balance breakdown">
        <li className="balance-card__chip">
          <span className="balance-card__chip-label">Period</span>
          <span className="balance-card__chip-value">
            {formatCoins(periodBalance)}
          </span>
        </li>
        <li className="balance-card__chip">
          <span className="balance-card__chip-label">Extra</span>
          <span className="balance-card__chip-value">
            {formatCoins(extraBalance)}
          </span>
        </li>
      </ul>

      {onGetMore && (
        <button
          type="button"
          className="balance-card__cta"
          onClick={onGetMore}
        >
          Get more coins
          <ArrowUpRight size={16} aria-hidden="true" />
        </button>
      )}
    </section>
  );
}
